<script lang="ts">
  import Modal from '../overlay/Modal.svelte';

  let {
    show = false,
    selectedDestination = '',
    onSelect,
    onClose,
  }: {
    show?: boolean;
    selectedDestination?: string;
    onSelect?: (name: string) => void;
    onClose?: () => void;
  } = $props();

  let activeTab: 'domestic' | 'international' = $state('domestic');
  let searchQuery = $state('');

  interface Destination {
    name: string;
    emoji: string;
    country?: string;
  }

  const domesticDestinations: Destination[] = [
    { name: '서울', emoji: '🏙️' },
    { name: '제주', emoji: '🏝️' },
    { name: '부산', emoji: '🌊' },
    { name: '강릉', emoji: '⛱️' },
    { name: '경주', emoji: '🏛️' },
    { name: '전주', emoji: '🏘️' },
    { name: '여수', emoji: '🌅' },
    { name: '속초', emoji: '🏔️' },
    { name: '춘천', emoji: '🍂' },
    { name: '양양', emoji: '🏄' },
    { name: '가평', emoji: '🌲' },
    { name: '대구', emoji: '🌸' },
    { name: '대전', emoji: '🚄' },
    { name: '광주', emoji: '🎨' },
    { name: '인천', emoji: '✈️' },
    { name: '울산', emoji: '🐋' },
  ];

  const internationalDestinations: Record<string, Destination[]> = {
    '일본': [
      { name: '도쿄', emoji: '🗼' },
      { name: '오사카', emoji: '🏯' },
      { name: '교토', emoji: '⛩️' },
      { name: '후쿠오카', emoji: '🍜' },
      { name: '삿포로', emoji: '❄️' },
      { name: '오키나와', emoji: '🏖️' },
      { name: '나고야', emoji: '🏰' },
    ],
    '동남아': [
      { name: '방콕', country: '태국', emoji: '🛕' },
      { name: '치앙마이', country: '태국', emoji: '🐘' },
      { name: '푸켓', country: '태국', emoji: '🏝️' },
      { name: '다낭', country: '베트남', emoji: '🌴' },
      { name: '호치민', country: '베트남', emoji: '🏍️' },
      { name: '하노이', country: '베트남', emoji: '🍲' },
      { name: '나트랑', country: '베트남', emoji: '🏊' },
      { name: '발리', country: '인도네시아', emoji: '🌺' },
      { name: '싱가포르', country: '싱가포르', emoji: '🦁' },
    ],
    '중화권': [
      { name: '홍콩', emoji: '🌃' },
      { name: '마카오', emoji: '🎰' },
      { name: '타이베이', country: '대만', emoji: '🧋' },
      { name: '상하이', country: '중국', emoji: '🌆' },
    ],
    '미주/유럽': [
      { name: '뉴욕', country: '미국', emoji: '🗽' },
      { name: 'LA', country: '미국', emoji: '🎬' },
      { name: '하와이', country: '미국', emoji: '🌈' },
      { name: '파리', country: '프랑스', emoji: '🗼' },
      { name: '런던', country: '영국', emoji: '🎡' },
      { name: '로마', country: '이탈리아', emoji: '🏛️' },
      { name: '바르셀로나', country: '스페인', emoji: '⚽' },
    ],
  };

  function selectDestination(name: string) {
    selectedDestination = name;
    onSelect?.(name);
    show = false;
  }

  function close() {
    show = false;
    onClose?.();
  }

  let filteredDomestic = $derived(
    domesticDestinations.filter(d =>
      d.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  let filteredInternational = $derived(
    Object.fromEntries(
      Object.entries(internationalDestinations).map(([country, cities]) => [
        country,
        cities.filter(c =>
          c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (c.country?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false)
        )
      ]).filter(([_, cities]) => (cities as Destination[]).length > 0)
    ) as Record<string, Destination[]>
  );
</script>

<Modal {show} title="목적지 선택" size="lg" onClose={close}>
  <div class="space-y-4 destination-picker">
    <!-- 검색 -->
    <div class="relative">
      <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 destination-picker__meta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input
        type="text"
        bind:value={searchQuery}
        placeholder="목적지 검색"
        class="w-full pl-10 pr-4 py-3 rounded-xl destination-picker__input"
      />
      {#if searchQuery}
        <button
          onclick={() => searchQuery = ''}
          class="absolute right-3 top-1/2 -translate-y-1/2 destination-picker__clear"
          aria-label="검색어 지우기"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      {/if}
    </div>

    <!-- 탭 -->
    <div class="flex rounded-xl p-1 destination-picker__tabs">
      <button
        onclick={() => activeTab = 'domestic'}
        class="flex-1 py-2.5 px-4 rounded-lg font-medium transition-all {activeTab === 'domestic' ? 'tab-active' : 'tab-inactive'}"
      >
        국내
      </button>
      <button
        onclick={() => activeTab = 'international'}
        class="flex-1 py-2.5 px-4 rounded-lg font-medium transition-all {activeTab === 'international' ? 'tab-active' : 'tab-inactive'}"
      >
        해외
      </button>
    </div>

    <!-- 목적지 목록 -->
    <div class="max-h-[400px] overflow-y-auto">
      {#if activeTab === 'domestic'}
        {#if filteredDomestic.length === 0}
          <div class="text-center py-8 destination-picker__meta">
            <svg class="w-12 h-12 mx-auto mb-3 destination-picker__muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            검색 결과가 없습니다
          </div>
        {:else}
          <div class="flex flex-wrap gap-2">
            {#each filteredDomestic as dest}
              <button
                onclick={() => selectDestination(dest.name)}
                class="dest-btn inline-flex items-center gap-2 px-4 py-2.5 rounded-full border-2 transition-all {selectedDestination === dest.name ? 'dest-selected' : ''}"
              >
                <span>{dest.emoji}</span>
                <span class="font-medium">{dest.name}</span>
              </button>
            {/each}
          </div>
        {/if}
      {:else}
        {#if Object.keys(filteredInternational).length === 0}
          <div class="text-center py-8 destination-picker__meta">
            <svg class="w-12 h-12 mx-auto mb-3 destination-picker__muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            검색 결과가 없습니다
          </div>
        {:else}
          <div class="space-y-6">
            {#each Object.entries(filteredInternational) as [region, cities]}
              <div>
                <h3 class="text-sm font-semibold mb-3 destination-picker__meta">{region}</h3>
                <div class="flex flex-wrap gap-2">
                  {#each cities as dest}
                    <button
                      onclick={() => selectDestination(dest.name)}
                      class="dest-btn inline-flex items-center gap-2 px-4 py-2.5 rounded-full border-2 transition-all {selectedDestination === dest.name ? 'dest-selected' : ''}"
                    >
                      <span>{dest.emoji}</span>
                      <span class="font-medium">{dest.name}</span>
                    </button>
                  {/each}
                </div>
              </div>
            {/each}
          </div>
        {/if}
      {/if}
    </div>

    <!-- 직접 입력 -->
    <div class="pt-4 border-t destination-picker__footer">
      {#if searchQuery.trim()}
        <button
          onclick={() => selectDestination(searchQuery.trim())}
          class="w-full py-2.5 px-4 rounded-lg font-medium transition-colors destination-picker__primary-button"
        >
          '{searchQuery.trim()}' 으로 직접 입력
        </button>
      {:else}
        <p class="text-sm destination-picker__meta text-center">
          원하는 목적지가 없나요? 검색창에 입력하고 "직접 입력" 버튼을 클릭하세요.
        </p>
      {/if}
    </div>
  </div>
</Modal>

<style>
  .destination-picker {
    color: var(--text-primary);
  }

  .max-h-\[400px\]::-webkit-scrollbar {
    width: 6px;
  }
  .max-h-\[400px\]::-webkit-scrollbar-track {
    background: color-mix(in srgb, var(--surface-hover) 78%, transparent);
    border-radius: 3px;
  }
  .max-h-\[400px\]::-webkit-scrollbar-thumb {
    background: color-mix(in srgb, var(--text-secondary) 32%, transparent);
    border-radius: 3px;
  }
  .max-h-\[400px\]::-webkit-scrollbar-thumb:hover {
    background: color-mix(in srgb, var(--text-secondary) 48%, transparent);
  }

  .destination-picker__meta {
    color: var(--text-secondary);
  }

  .destination-picker__muted {
    color: var(--text-muted);
  }

  .destination-picker__input {
    background: var(--surface);
    color: var(--text-primary);
    border: 1px solid var(--border-bright);
  }

  .destination-picker__input::placeholder {
    color: var(--text-muted);
  }

  .destination-picker__input:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 18%, transparent);
  }

  .destination-picker__clear {
    color: var(--text-muted);
  }

  .destination-picker__clear:hover {
    color: var(--text-secondary);
  }

  .destination-picker__tabs {
    background: color-mix(in srgb, var(--surface-hover) 88%, transparent);
  }

  .tab-active {
    background: color-mix(in srgb, var(--accent) 16%, var(--surface));
    color: var(--accent);
    box-shadow: 0 0 10px color-mix(in srgb, var(--accent) 18%, transparent);
  }
  .tab-inactive {
    color: var(--text-secondary);
  }
  .tab-inactive:hover {
    color: var(--text-primary);
    background: color-mix(in srgb, var(--surface-hover) 92%, transparent);
  }

  .dest-btn {
    background: var(--surface-hover);
    border-color: var(--border);
    color: var(--text-secondary);
  }
  .dest-btn:hover {
    border-color: color-mix(in srgb, var(--accent) 50%, var(--border));
    background: color-mix(in srgb, var(--accent-dim) 70%, var(--surface-hover));
    color: var(--text-primary);
  }
  .dest-btn.dest-selected {
    background: color-mix(in srgb, var(--accent) 18%, var(--surface));
    border-color: var(--accent);
    color: var(--accent);
    box-shadow: 0 0 12px color-mix(in srgb, var(--accent) 20%, transparent);
  }

  .destination-picker__footer {
    border-color: var(--border);
  }

  .destination-picker__primary-button {
    background: var(--accent);
    color: #fff;
  }

  .destination-picker__primary-button:hover {
    background: var(--accent-hover);
  }
</style>
